import { useState, useEffect } from 'react';
import { NotificationAPI } from '@/apis/NotificationAPI';
import { ConfigurationAPI } from '@/apis/ConfigurationAPI';
import { getUserFromCookie } from '@/utils/utilities';
import { EXPERIENCE_ACTIONS } from '@/utils/permissionGateConfig';
import { getPoliciesFromSession } from '@/components/PermissionGate';
const SURVEY_NOTIFICATION_INTERVAL_DAYS = 15;
const MS_PER_DAY = 1000 * 60 * 60 * 24;
const REQUIRED_ACTIONS = [
    EXPERIENCE_ACTIONS.EXPERIENCE_CREATE_SURVEY_RESPONSE,
    EXPERIENCE_ACTIONS.EXPERIENCE_VIEW_SURVEY_RESPONSE
];
// Check if the given date is more than specified days ago
const isMoreThanDaysAgo = (dateString, days) => {
    const diffTime = Date.now() - new Date(dateString).getTime();
    return diffTime / MS_PER_DAY > days;
};
// Check if user has all required actions in their policies
const hasRequiredActions = (policies) => {
    if (!policies || policies.length === 0)
        return false;
    // Collect all actions from all policies' resource_action arrays
    const allActions = [];
    for (const policy of policies) {
        const resourceActions = policy?.resource_action ?? [];
        for (const ra of resourceActions) {
            const actions = ra?.action ?? [];
            allActions.push(...actions);
        }
    }
    // Check if all required actions are present
    return REQUIRED_ACTIONS.every(action => allActions.includes(action));
};
// Determine if survey notification should be shown based on last response
const shouldShowBasedOnLastResponse = (lastUpdated) => {
    if (!lastUpdated)
        return true;
    return isMoreThanDaysAgo(lastUpdated, SURVEY_NOTIFICATION_INTERVAL_DAYS);
};
/**
 * Hook to determine if the survey notification should be shown to the user.
 *
 * Logic:
 * 1. Get user's policies from session storage and check if user has required actions
 * 2. If authorized, fetch the user's last survey response date
 * 3. If no survey exists OR last survey was more than 15 days ago, return true
 *
 * @returns Object containing shouldShowSurveyNotification boolean and isSurveyLoading
 */
export const useSurveyNotification = () => {
    const [shouldShowSurveyNotification, setShouldShowSurveyNotification] = useState(false);
    const [isSurveyLoading, setIsSurveyLoading] = useState(true);
    useEffect(() => {
        const checkSurveyNotification = async () => {
            const userId = getUserFromCookie()?.id;
            if (!userId) {
                setIsSurveyLoading(false);
                return;
            }
            try {
                // Get policies from session storage using shared utility
                const policies = getPoliciesFromSession();
                if (!hasRequiredActions(policies)) {
                    setIsSurveyLoading(false);
                    return;
                }
                const platformHost = await ConfigurationAPI.getPlatformHost();
                const { lastUpdated } = await NotificationAPI.getLastSurveyResponse(userId, platformHost);
                setShouldShowSurveyNotification(shouldShowBasedOnLastResponse(lastUpdated));
            }
            catch (error) {
                console.error('Error checking survey notification eligibility:', error);
            }
            finally {
                setIsSurveyLoading(false);
            }
        };
        checkSurveyNotification();
    }, []);
    return { shouldShowSurveyNotification, isSurveyLoading };
};
